import { useRef, useState } from 'react';

import Card from '@mui/material/Card';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';

import { useDebounce } from 'src/hooks/use-debounce';
import { useHasPermission } from 'src/hooks/use-has-permission';

import {
  useCustomerCustomFieldsQuery,
  useCustomerMetadataSampleQuery,
  type CustomerCustomFieldsQuery,
  type SaveCustomerCustomFieldInput,
  useSaveCustomerCustomFieldMutation,
  useDeleteCustomerCustomFieldMutation,
  useCustomerMetadataSampleCustomersQuery,
  type CustomerMetadataSampleCustomersQuery,
} from 'src/graphql/generated';

import { useSnackbar } from 'src/components/snackbar';
import { ConfirmDialog } from 'src/components/custom-dialog';

const FIELD_TYPES = {
  TEXT: 'Texto',
  MULTISELECT: 'Seleção múltipla',
  NUMBER: 'Número',
  DATE: 'Data',
  BOOLEAN: 'Sim/não',
  STRUCTURED: 'Lista ou objeto',
};
const FIELD_TYPE_HELP: Record<string, string> = {
  DATE: 'Datas: AAAA-MM-DD ou DD/MM/AAAA.',
  MULTISELECT: 'No filtro, permite selecionar vários valores cadastrados nos clientes.',
  TEXT: 'No filtro, permite digitar livremente o texto da pesquisa.',
};
type Field = CustomerCustomFieldsQuery['customerCustomFields'][number];
type SampleCustomer = NonNullable<
  NonNullable<
    NonNullable<CustomerMetadataSampleCustomersQuery['allCustomersRelay']>['edges'][number]
  >['node']
>;

function formatMetadataPath(path: Field['metadataPath']) {
  return path.reduce<string>((text, segment) => {
    if (typeof segment === 'number') return `${text}[${segment}]`;
    const key =
      /[.[\]›]/.test(segment) || /^\d+$/.test(segment) ? JSON.stringify(segment) : segment;
    return text ? `${text} › ${key}` : key;
  }, '');
}

export default function CustomerCustomFieldsConfig() {
  const { hasAdminPermission, hasEmployeePermission, data, isLoading } = useHasPermission();
  const storeId = data?.me?.connectedStore?.id;
  if (isLoading) return <CircularProgress size={24} aria-label="Carregando permissões" />;
  if (!storeId) return null;
  return (
    <CustomerCustomFieldsEditor
      key={storeId}
      canManage={!!(hasAdminPermission || hasEmployeePermission)}
    />
  );
}

function CustomerCustomFieldsEditor({ canManage }: { canManage: boolean }) {
  const formRef = useRef<HTMLFormElement>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { data, loading, error, refetch } = useCustomerCustomFieldsQuery({
    fetchPolicy: 'network-only',
  });
  const [saveField, { loading: saving }] = useSaveCustomerCustomFieldMutation();
  const [deletingField, setDeletingField] = useState<Field | null>(null);
  const [deleteField, { loading: deleting }] = useDeleteCustomerCustomFieldMutation();
  const busy = saving || deleting;
  const [label, setLabel] = useState('');
  const [fieldType, setFieldType] = useState('TEXT');
  const [editingField, setEditingField] = useState<Field | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [sampleCustomer, setSampleCustomer] = useState<SampleCustomer | null>(null);
  const [path, setPath] = useState<Field['metadataPath']>([]);
  const customerSearch = useCustomerMetadataSampleCustomersQuery({
    variables: { search: debouncedSearch || undefined },
    skip: !canManage,
    fetchPolicy: 'network-only',
  });
  const customers = (customerSearch.data?.allCustomersRelay?.edges ?? []).flatMap((edge) =>
    edge?.node ? [edge.node] : []
  );
  const sample = useCustomerMetadataSampleQuery({
    variables: { customerId: String(sampleCustomer?.pk ?? '') },
    skip: !canManage || !sampleCustomer,
    fetchPolicy: 'network-only',
  });
  const currentSample =
    sample.data?.customerMetadataSample?.customerId === String(sampleCustomer?.pk)
      ? sample.data.customerMetadataSample
      : undefined;
  const options = !sample.loading && !sample.error ? currentSample?.fields ?? [] : [];
  const source =
    options.find((option) => JSON.stringify(option.path) === JSON.stringify(path)) ?? null;
  const canSave =
    !!label.trim() && !!source && !!sampleCustomer && !sample.loading && !sample.error;

  const cancel = () => {
    setEditingField(null);
    setLabel('');
    setFieldType('TEXT');
    setPath([]);
  };

  const save = async (input: SaveCustomerCustomFieldInput, resetForm = false) => {
    try {
      await saveField({ variables: { input } });
      if (resetForm) cancel();
      enqueueSnackbar('Mapeamento salvo.', { variant: 'success' });
    } catch (saveError) {
      enqueueSnackbar(saveError?.message || 'Não foi possível salvar o mapeamento.', {
        variant: 'error',
      });
      return;
    }
    try {
      await refetch();
    } catch {
      /* The query renders its own retry state. */
    }
  };

  const remove = async () => {
    if (!deletingField || busy) return;
    try {
      const result = await deleteField({ variables: { fieldId: deletingField.id } });
      if (!result.data?.deleteCustomerCustomField?.success)
        throw new Error('Não foi possível excluir o campo.');
      setDeletingField(null);
      enqueueSnackbar('Campo personalizado excluído.', { variant: 'success' });
    } catch (deleteError) {
      enqueueSnackbar(deleteError?.message || 'Não foi possível excluir o campo.', {
        variant: 'error',
      });
      return;
    }
    await refetch().catch(() => undefined);
  };

  return (
    <Card sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Stack spacing={0.5}>
          <Typography variant="h6" component="h2">
            Campos personalizados de clientes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Escolha um cliente de exemplo e relacione uma chave do metadata a um campo do Zeki. O
            mapeamento vale para todos os clientes desta loja, usando os valores atuais de cada um.
            Nas listas, selecione uma posição: [0] é o primeiro item, [1] é o segundo. Se a ordem
            mudar na origem, o campo passa a mostrar o novo item daquela posição.
          </Typography>
        </Stack>
        {canManage && (
          <Stack
            component="form"
            ref={formRef}
            spacing={2}
            onSubmit={(event) => {
              event.preventDefault();
              if (canSave && !busy)
                save(
                  {
                    fieldId: editingField?.id,
                    label: label.trim(),
                    fieldType,
                    isActive: editingField?.isActive ?? true,
                    metadataPath: path,
                    sampleCustomerId: String(sampleCustomer?.pk),
                  },
                  true
                );
            }}
          >
            <Autocomplete
              options={customers}
              value={sampleCustomer}
              loading={customerSearch.loading}
              disabled={busy}
              filterOptions={(items) => items}
              isOptionEqualToValue={(option, value) => option.pk === value.pk}
              getOptionLabel={(option) =>
                `${option.name || 'Sem nome'} (#${option.pk})${
                  option.email ? ` — ${option.email}` : ''
                }`
              }
              onInputChange={(_, value, reason) => {
                if (reason === 'input' || reason === 'clear') setSearch(value);
              }}
              onChange={(_, value) => {
                setSampleCustomer(value);
                setPath(editingField?.metadataPath ?? []);
              }}
              noOptionsText="Nenhum cliente encontrado"
              loadingText="Buscando clientes…"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cliente de exemplo"
                  helperText="Busque pelo nome ou e-mail. Exibimos até 20 clientes por busca."
                />
              )}
            />
            {customerSearch.error && (
              <Alert
                severity="error"
                action={<Button onClick={() => customerSearch.refetch()}>Tentar novamente</Button>}
              >
                Não foi possível buscar os clientes.
              </Alert>
            )}
            {sampleCustomer && sample.loading && (
              <CircularProgress size={24} aria-label="Carregando metadata" />
            )}
            {sampleCustomer && sample.error && (
              <Alert
                severity="error"
                action={<Button onClick={() => sample.refetch()}>Tentar novamente</Button>}
              >
                Não foi possível ler o metadata deste cliente.
              </Alert>
            )}
            {sampleCustomer && !sample.loading && !sample.error && currentSample && (
              <>
                {!options.length && (
                  <Alert severity="info">
                    Este cliente não tem campos disponíveis no metadata. Selecione outro cliente ou
                    aguarde a importação dos dados.
                  </Alert>
                )}
                {currentSample.truncated && (
                  <Alert severity="info">
                    A amostra atingiu o limite de leitura. São exibidas até 200 chaves.
                  </Alert>
                )}
                {!!currentSample.unsupportedCount && (
                  <Typography variant="body2" color="text.secondary">
                    Alguns caminhos excedem 8 níveis ou têm chaves muito longas. Selecione um objeto
                    pai para exibir seu conteúdo.
                  </Typography>
                )}
                {!!options.length && (
                  <Autocomplete
                    options={options}
                    value={source}
                    disabled={busy}
                    isOptionEqualToValue={(option, value) =>
                      JSON.stringify(option.path) === JSON.stringify(value.path)
                    }
                    getOptionLabel={(option) => formatMetadataPath(option.path)}
                    onChange={(_, value) => {
                      setPath(value?.path ?? []);
                      if (value) {
                        setFieldType(value.suggestedType);
                        if (!label.trim())
                          setLabel(String(value.path[value.path.length - 1]).slice(0, 100));
                      }
                    }}
                    noOptionsText="Nenhuma chave encontrada"
                    renderInput={(params) => (
                      <TextField {...params} label="Chave do metadata" required />
                    )}
                  />
                )}
                {source && (
                  <Stack
                    component="section"
                    aria-label="Prévia do valor"
                    aria-live="polite"
                    spacing={0.5}
                    sx={{ p: 2, borderRadius: 1, bgcolor: 'background.neutral' }}
                  >
                    <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>
                      Prévia — {sampleCustomer.name || 'Cliente sem nome'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ overflowWrap: 'anywhere' }}
                    >
                      {formatMetadataPath(source.path)}
                    </Typography>
                    <Typography
                      variant="h6"
                      component="p"
                      sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}
                    >
                      {source.preview}
                      {source.previewTruncated ? '…' : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Valor atual no metadata deste cliente. Confira antes de salvar o mapeamento.
                      {source.previewTruncated && ' Prévia limitada a 200 caracteres.'}
                    </Typography>
                  </Stack>
                )}
              </>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                required
                label="Nome do campo no Zeki"
                value={label}
                disabled={busy}
                inputProps={{ maxLength: 100 }}
                onChange={(event) => setLabel(event.target.value)}
              />
              <TextField
                select
                fullWidth
                label="Tipo"
                value={fieldType}
                disabled={busy}
                helperText={FIELD_TYPE_HELP[fieldType]}
                onChange={(event) => setFieldType(event.target.value)}
              >
                {Object.entries(FIELD_TYPES)
                  .filter(
                    ([value]) => value !== 'STRUCTURED' || editingField?.fieldType === 'STRUCTURED'
                  )
                  .map(([value, name]) => (
                    <MenuItem key={value} value={value}>
                      {name}
                    </MenuItem>
                  ))}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {editingField && (
                <Button disabled={busy} onClick={cancel}>
                  Cancelar edição
                </Button>
              )}
              <LoadingButton
                type="submit"
                variant="contained"
                loading={saving}
                disabled={!canSave || busy}
              >
                {editingField ? `Salvar ${editingField.label}` : 'Criar mapeamento'}
              </LoadingButton>
            </Stack>
          </Stack>
        )}
        <Divider />
        {loading && <CircularProgress size={24} aria-label="Carregando mapeamentos" />}
        {error && (
          <Alert
            severity="error"
            action={<Button onClick={() => refetch()}>Tentar novamente</Button>}
          >
            Não foi possível carregar os mapeamentos.
          </Alert>
        )}
        {!loading && !error && !data?.customerCustomFields.length && (
          <Typography color="text.secondary">Nenhum campo mapeado.</Typography>
        )}
        {!error &&
          data?.customerCustomFields.map((field) => (
            <Stack
              key={field.id}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
            >
              <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography sx={{ overflowWrap: 'anywhere' }}>{field.label}</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ overflowWrap: 'anywhere' }}
                >
                  {formatMetadataPath(field.metadataPath)} ·{' '}
                  {FIELD_TYPES[field.fieldType as keyof typeof FIELD_TYPES]}
                </Typography>
              </Stack>
              {canManage ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    disabled={busy}
                    aria-label={`Editar ${field.label}`}
                    onClick={() => {
                      setEditingField(field);
                      setLabel(field.label);
                      setFieldType(field.fieldType);
                      setPath(field.metadataPath);
                      formRef.current?.scrollIntoView({ block: 'start' });
                    }}
                  >
                    Editar
                  </Button>
                  <Button
                    disabled={busy}
                    color="error"
                    aria-label={`Excluir ${field.label}`}
                    onClick={() => setDeletingField(field)}
                  >
                    Excluir
                  </Button>
                  <FormControlLabel
                    label={field.isActive ? 'Ativo' : 'Inativo'}
                    control={
                      <Switch
                        checked={field.isActive}
                        disabled={busy}
                        inputProps={{ 'aria-label': `Ativar ${field.label}` }}
                        onChange={(_, isActive) =>
                          save({
                            fieldId: field.id,
                            label: field.label,
                            fieldType: field.fieldType,
                            isActive,
                          })
                        }
                      />
                    }
                  />
                </Stack>
              ) : (
                <Typography variant="body2">{field.isActive ? 'Ativo' : 'Inativo'}</Typography>
              )}
            </Stack>
          ))}
        <Typography variant="body2" color="text.secondary">
          Desativar um campo apenas o oculta no detalhe. Os dados originais continuam no metadata.
        </Typography>
      </Stack>
      <ConfirmDialog
        open={!!deletingField}
        onClose={() => {
          if (!deleting) setDeletingField(null);
        }}
        title="Excluir campo personalizado?"
        content={`O campo “${
          deletingField?.label ?? ''
        }” deixará de aparecer nos clientes desta loja. Os dados originais do metadata serão preservados. Para exibi-lo novamente, crie um novo mapeamento.`}
        action={
          <LoadingButton
            color="error"
            variant="contained"
            loading={deleting}
            disabled={saving}
            onClick={remove}
          >
            Excluir campo
          </LoadingButton>
        }
      />
    </Card>
  );
}
